import { useState } from 'react';
import { Upload, FileText, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  usePaymentSettings,
  useCreatePayment,
  type PaymentBankInfo,
} from '@/hooks/usePayments';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invitationId: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

const formatBs = (n: number) =>
  new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const BANK_FIELDS: { key: keyof PaymentBankInfo; label: string }[] = [
  { key: 'holder', label: 'Titular' },
  { key: 'bank', label: 'Banco' },
  { key: 'account', label: 'Cuenta' },
  { key: 'ci', label: 'CI / RIF' },
  { key: 'phone', label: 'Teléfono' },
];

const PaymentModal = ({ open, onOpenChange, invitationId }: Props) => {
  const { data: settings, isLoading: loadingSettings } = usePaymentSettings();
  const createPayment = useCreatePayment();

  const [file, setFile] = useState<File | null>(null);
  const [bankReference, setBankReference] = useState('');
  const [notes, setNotes] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const amountUsd = settings?.publishPriceUsd ?? 0;
  const amountBs = (settings?.publishPriceUsd ?? 0) * (settings?.usdToBsRate ?? 0);

  const resetForm = () => {
    setFile(null);
    setBankReference('');
    setNotes('');
  };

  const handleOpenChange = (next: boolean) => {
    if (createPayment.isPending) return; // don't close mid-upload
    if (!next) resetForm();
    onOpenChange(next);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!ALLOWED_TYPES.includes(selected.type)) {
      toast.error('Formato no válido. Sube una imagen JPG/PNG o un PDF.');
      e.target.value = '';
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      toast.error('El archivo no debe superar 5 MB.');
      e.target.value = '';
      return;
    }
    setFile(selected);
  };

  const handleCopy = async (field: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField((c) => (c === field ? null : c)), 1500);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Adjunta el comprobante de pago.');
      return;
    }
    if (amountUsd <= 0) {
      toast.error('Aún no hay un precio configurado. Intenta más tarde.');
      return;
    }
    try {
      await createPayment.mutateAsync({
        invitationId,
        amountUsd,
        amountBs,
        file,
        bankReference,
        notes,
      });
      resetForm();
      onOpenChange(false);
    } catch (err) {
      // Keep the modal open so the user can retry without re-entering data.
      const message = err instanceof Error ? err.message : 'No se pudo registrar el pago';
      toast.error(message);
    }
  };

  const bankInfo = settings?.bankInfo;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Publicar invitación</DialogTitle>
          <DialogDescription>
            Transfiere el monto, sube el comprobante y publicaremos tu invitación al aprobar el pago.
          </DialogDescription>
        </DialogHeader>

        {loadingSettings ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Amount */}
            <div className="rounded-lg bg-muted p-4 text-center">
              <p className="text-sm text-muted-foreground">Monto a transferir</p>
              <p className="text-2xl font-semibold">{formatBs(amountBs)} Bs</p>
              <p className="text-xs text-muted-foreground">
                ≈ USD {amountUsd.toFixed(2)} (tasa {settings?.usdToBsRate ?? 0} Bs/USD)
              </p>
            </div>

            {/* Bank info */}
            {bankInfo && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Datos para la transferencia</p>
                <div className="rounded-lg border divide-y">
                  {BANK_FIELDS.map(({ key, label }) => {
                    const value = bankInfo[key];
                    if (!value) return null;
                    return (
                      <div key={key} className="flex items-center justify-between gap-2 px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">{label}</p>
                          <p className="truncate text-sm font-medium">{value}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="shrink-0"
                          onClick={() => handleCopy(key, value)}
                          aria-label={`Copiar ${label}`}
                        >
                          {copiedField === key ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
                {bankInfo.qr_url && (
                  <div className="flex justify-center pt-2">
                    <img
                      src={bankInfo.qr_url}
                      alt="Código QR para pago móvil"
                      className="h-40 w-40 rounded-lg border object-contain"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Reference */}
            <div className="space-y-2">
              <Label htmlFor="bank-reference">Referencia bancaria (opcional)</Label>
              <Input
                id="bank-reference"
                value={bankReference}
                onChange={(e) => setBankReference(e.target.value)}
                placeholder="Nº de referencia de la transferencia"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="payment-notes">Notas (opcional)</Label>
              <Textarea
                id="payment-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Cualquier detalle que debamos saber"
                rows={2}
              />
            </div>

            {/* Uploader */}
            <div className="space-y-2">
              <Label>Comprobante de pago</Label>
              <div className="rounded-lg border-2 border-dashed p-6 text-center">
                <input
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="receipt-upload"
                />
                <label htmlFor="receipt-upload" className="cursor-pointer">
                  {file ? (
                    <span className="flex items-center justify-center gap-2 text-sm font-medium">
                      <FileText className="h-5 w-5 text-primary" />
                      {file.name}
                    </span>
                  ) : (
                    <>
                      <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                      <p className="text-sm font-medium">Haz clic para subir el comprobante</p>
                      <p className="text-xs text-muted-foreground">JPG, PNG o PDF · máx. 5 MB</p>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={createPayment.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={createPayment.isPending || loadingSettings || !file}>
            {createPayment.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enviar comprobante
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
